import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Flex,
    FlexItem,
    Text,
    Header,
    Divider,
    TextArea,
    Button,
    Segment,
    SendIcon,
    Alert,
    ChevronStartIcon,
    Ref,
} from "@fluentui/react-northstar";
import { CloseIcon } from "@fluentui/react-icons-northstar";
import { useStore } from "stores/Stores";
import { format } from "date-fns";
import { pl as plLocale } from "date-fns/locale";
import { Loader, useScrollLock } from "ecom-teams-apps-core";
import styles from "./taskDetails.module.scss";
import pl from "shared/messages/pl";
import TaskDetailsItem from "./TaskDetailsItem";
import TaskDetailsDropdownItem from "./TaskDetailsDropdownItem";
import { constants } from "shared/constants";
import { TaskAttribute } from "shared/models/TaskAttribute";
import { DateRange } from "shared/models/DateRange";
import Comment from "./Comment";
import TaskDetailsEditableDateItem from "./TaskDetailsEditableDateItem";
import { UserIdentity } from "shared/models/UserIdentity";
import { BusinessRole } from "shared/enums/BusinessRole";
import { runInAction } from "mobx";
import classNames from "classnames";
import Attachment from "./Attachment";
import { HostClientType } from "@microsoft/teams-js";
import DropUpload from "./DropUpload";
import MobileUpload from "./MobileUpload";
import { TaskStatus } from "shared/models/TaskStatus";
import { EditConfirmationButtonsTypes } from "./EditConfirmationButtons";
import TaskDetailsNodeItem from "./TaskDetailsNodeItem";
import { DictionaryItemDto } from "tasksBoard/service/api/DictionaryItemDto";
import { TaskAttributeMulti } from "shared/models/TaskAttributeMulti";
import { CloseTaskEventSource } from "appilication-insights/events/CloseTask/CloseTaskEventSource";
import { MsTeamsContext } from "msTeams/store";
import TagLabels from "msTeamsUI/components/Board/TagLabels";

export interface TaskDetailsProps {
    taskId: number;
    dateRange: DateRange;
    isMobile: boolean;

    onCancel: () => void;
    onTaskClosed?: (source: CloseTaskEventSource) => void;
}

const TasksDetails: React.FC<TaskDetailsProps> = observer((props) => {
    const msTeamsContext = useContext(MsTeamsContext);

    const { tasksStore, taskStatusesStore, authorizationStore, configStore } = useStore();
    const [comment, setComment] = useState("");
    const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(true);
    const taskDetailsRef = useRef<HTMLDivElement | null>(null);
    const taskDetailsContentRef = useRef<HTMLDivElement | null>(null);

    useScrollLock(props.isMobile && props.taskId !== 0);

    const loadTaskData = useCallback(async () => {
        setIsLoadingTaskDetails(true);
        // Don't change the order of loading. loadAvailableAssignees should be before loadTask
        // because there is a problem with renderning assignee user in TaskDetailsDropdownItem
        tasksStore.loadAvailableAssignees(props.taskId);
        tasksStore.loadTask(props.taskId);
        setIsLoadingTaskDetails(false);
    }, [tasksStore, props.taskId]);

    useEffect(() => {
        loadTaskData();
    }, [loadTaskData]);

    useEffect(() => {
        taskDetailsRef?.current?.scrollTo(0, 0);
        taskDetailsContentRef?.current?.scrollTo(0, 0);
    }, [tasksStore.requestStatusesWithError.length]);

    const formatDate = (date: Date) => {
        return format(date, constants.taskDetailsDateFormat, { locale: plLocale });
    };

    const getTaskStatusItemIndex = (taskStatusId?: number) => {
        return taskStatusesStore.taskStatuses["TASK_STATUS"].findIndex((x) => x.id === taskStatusId);
    };

    const getTaskAssigneeItemIndex = (assignee?: UserIdentity) => {
        return tasksStore.availableAssignees.findIndex((x) => x.userName === assignee?.userName);
    };

    const getAttributeDefaultItemIndex = (attribute?: TaskAttribute) => {
        return attribute?.action && attribute.action.orderIndex - 1;
    };

    const getTaskStatusesForTaskType = (attributeName: string, taskTypeCode: string): TaskStatus[] => {
        const attName = attributeName.split("_MULTI")[0];
        const storeStatuses = taskStatusesStore.taskStatuses[attName];
        return storeStatuses.filter((s) => s.flexFilter1 !== null && s.flexFilter1!.find((s) => s === taskTypeCode));
    };

    const updateMultiAttributeFilteredDictionary = async (
        attributeName: string,
        taskTypeCode: string,
        values: string[],
        attribute?: TaskAttributeMulti
    ) => {
        const aviailableValues = getTaskStatusesForTaskType(attributeName, taskTypeCode);
        const attributeIndexes: number[] = [];

        values.forEach((value) => {
            const item = aviailableValues.find((av) => av.description === value);
            if (!item) {
                return;
            }
            const attName = attributeName.split("_MULTI")[0];
            const attributeIndex = taskStatusesStore.taskStatuses[attName].findIndex((i) => i.id === item.id);
            attributeIndexes.push(attributeIndex);
        });

        const result = await updateAttributeMulti(attributeName, attributeIndexes, attribute);

        return false;
    };

    const updateAttribute = async (attributeName: string, attributeIndex: number, attribute?: TaskAttribute) => {
        await tasksStore.updateTaskAttribute(tasksStore.selectedTask!.id, attribute!.id, {
            ...taskStatusesStore.taskStatuses[attributeName][attributeIndex],
            deleted: false,
            flexField1: "",
            flexFilter1: [],
        });

        if (tasksStore.updateTaskAttributeRequestStatus.isDone && tasksStore.updateTaskAttributeRequestStatus.error) {
            return false;
        }

        runInAction(() => {
            attribute!.action = taskStatusesStore.taskStatuses[attributeName][attributeIndex];
        });

        return true;
    };

    const updateAttributeMulti = async (
        attributeName: string,
        attributeIndexes: number[],
        attribute?: TaskAttributeMulti
    ) => {
        const customValues: DictionaryItemDto[] = [];
        const attName = attributeName.split("_MULTI")[0];

        attributeIndexes.forEach((attIdx) => {
            const toAdd = {
                ...taskStatusesStore.taskStatuses[attName][attIdx],
                deleted: false,
                flexField1: "",
                flexFilter1: [],
            };
            customValues.push(toAdd);
        });

        await tasksStore.updateTaskAttributeMulti(tasksStore.selectedTask!.id, attribute!.id, customValues);

        if (tasksStore.updateTaskAttributeRequestStatus.isDone && tasksStore.updateTaskAttributeRequestStatus.error) {
            return false;
        }

        runInAction(() => {
            attribute!.action = [];
            attributeIndexes.forEach((attIdx) => {
                attribute!.action.push(taskStatusesStore.taskStatuses[attName][attIdx]);
            });
        });

        return true;
    };

    const closeTask = async () => {
        const status = taskStatusesStore.taskStatuses["TASK_STATUS"].find(
            (s) => s.code === constants.taskStatuses.closed
        );
        if (status) {
            return await updateTaskStatus(status, CloseTaskEventSource.Button);
        }
    };

    const updateTaskStatusDropDown = async (selectedIndex: number) => {
        const status = taskStatusesStore.taskStatuses["TASK_STATUS"][selectedIndex];
        return await updateTaskStatus(status, CloseTaskEventSource.Dropdown);
    };

    const updateTaskStatus = async (taskStatus: TaskStatus, source: CloseTaskEventSource) => {
        await tasksStore.updateTaskStatus(tasksStore.selectedTask!.id, 0, taskStatus.code, taskStatus.id);

        if (tasksStore.updateTaskStatusRequestStatus.isDone && tasksStore.updateTaskStatusRequestStatus.error) {
            return false;
        }

        runInAction(() => {
            tasksStore.selectedTask!.status = tasksStore.tasks.find(
                (x) => x.id === tasksStore.selectedTask!.id
            )?.status!;
        });

        if (props.onTaskClosed && taskStatus.code === constants.taskStatuses.closed) {
            props.onTaskClosed(source);
        }

        return true;
    };

    const updateTaskAssignee = async (selectedIndex: number) => {
        const assignee = tasksStore.availableAssignees[selectedIndex];
        await tasksStore.assignUser(tasksStore.selectedTask!.id, assignee.userName);
        await tasksStore.loadTasks(props.dateRange);

        if (tasksStore.assignUserRequestStatus.isDone && tasksStore.assignUserRequestStatus.error) {
            return false;
        }

        props.onCancel();

        return true;
    };

    const updatePlannedDate = async (date: Date) => {
        await tasksStore.updatePlannedDate(tasksStore.selectedTask!.id, date);

        if (tasksStore.updatePlannedDateRequestStatus.isDone && tasksStore.updatePlannedDateRequestStatus.error) {
            return false;
        }

        runInAction(() => {
            tasksStore.selectedTask!.plannedDate = date;
        });

        if (date < props.dateRange.start || date > props.dateRange.end) {
            await tasksStore.loadTasks(props.dateRange);
            props.onCancel();
        }

        return true;
    };

    const removeAttachment = async (attachmentId: number) => {
        await tasksStore.removeAttachment(tasksStore.selectedTask!.id, attachmentId);
    };

    const uploadAttachment = async (files: File[]) => {
        if (!files.length) {
            return;
        }

        const file = files[0];

        const result = await tasksStore.uploadFile(file);

        if (!result) {
            return;
        }

        await tasksStore.addAttachment(tasksStore.selectedTask!.id, result);
    };

    const getFile = async (filename: string, attachmentHash: string) => {
        const token = await tasksStore.getFileToken(attachmentHash);

        if (!token) {
            return;
        }

        const isMobileClient =
            msTeamsContext.teamsClientType === HostClientType.android ||
            msTeamsContext.teamsClientType === HostClientType.ios;

        if (isMobileClient) {
            window.open(`${process.env.REACT_APP_API_BASE_URL}${constants.filesDownloadUrl}/${token}`, "_blank");
            return;
        }

        await tasksStore.retrieveFile(filename, token);
    };

    const renderDivider = () => {
        return props.isMobile && <Divider size={0.5} />;
    };

    const mapLoadingReasonToText = () => {
        if (isLoadingTaskDetails || tasksStore.selectedTask === null || tasksStore.loadTaskRequestStatus.isPending) {
            return pl.taskDetails.loading;
        }

        if (tasksStore.removeAttachmentRequestStatus.isPending) {
            return pl.taskDetails.attachments.loading.remove;
        }

        return pl.taskDetails.attachments.loading.retreive;
    };

    const isTaskCloseable = (): boolean => {
        const task = tasksStore.selectedTask;

        if (!task?.operationList) {
            return false;
        }

        return task.operationList.findIndex((o) => o.toLowerCase() === "close") >= 0 && task.status.code !== "CLOSED";
    };

    const isQuickActionsAvailable = (): boolean => {
        return isTaskCloseable();
    };

    const getQuickActions = (): ReactNode => {
        return (
            <Flex>
                {isTaskCloseable() && (
                    <Button primary size="small" onClick={closeTask}>
                        {pl.taskDetails.closeTask}
                    </Button>
                )}
            </Flex>
        );
    };

    const renderLoader = () => {
        return (
            <Segment className={styles.loaderContainer}>
                <Loader text={mapLoadingReasonToText()} />
            </Segment>
        );
    };

    const renderContent = () => {
        const taskHasInformativeType =
            tasksStore.selectedTask?.type.code === constants.taskTypesDictionaryValues.informative;

        return (
            <>
                <Flex space="between">
                    <Flex column vAlign="start" className={props.isMobile ? styles.mobileHeader : styles.desktopHeader}>
                        <FlexItem grow>
                            <Header as="h3" className={styles.headerSubject}>
                                <Text weight="semibold">{tasksStore.selectedTask!.subject}</Text>
                            </Header>
                        </FlexItem>
                        <FlexItem>
                            <Text disabled weight="semibold" size="medium">
                                {tasksStore.selectedTask!.externalReferenceId}
                            </Text>
                        </FlexItem>
                    </Flex>
                    <Button
                        iconOnly={true}
                        className={props.isMobile ? styles.mobileHeaderButton : ""}
                        icon={props.isMobile ? <ChevronStartIcon size="large" /> : <CloseIcon size="large" />}
                        onClick={props.onCancel}
                    />
                </Flex>
                {props.isMobile && <Divider size={1} fitted className={styles.headerDivider} />}
                <Ref innerRef={taskDetailsContentRef}>
                    <Flex column gap="gap.medium" className={styles.content}>
                        {tasksStore.requestStatusesWithError.length > 0 && (
                            <Alert
                                danger
                                dismissible
                                dismissAction={{
                                    "aria-label": pl.common.close,
                                    onClick: () => {
                                        tasksStore.resetAllRequestStatuses();
                                    },
                                }}
                                content={tasksStore.requestStatusesWithError[0]?.error}
                                className={styles.alertWrapper}
                                styles={{ padding: "6px 8px" }}
                            />
                        )}
                        <TagLabels tags={tasksStore.selectedTask!.tags} />
                        <TaskDetailsItem
                            label={pl.taskDetails.labels.description}
                            primaryText={tasksStore.selectedTask!.description}
                            isMobile={props.isMobile}
                            onlyDescription
                            additionalClassName={props.isMobile ? styles.mobileDescription : ""}
                        />
                        {renderDivider()}
                        <TaskDetailsDropdownItem
                            onChange={(index) => updateTaskAssignee(index)}
                            label={pl.taskDetails.labels.assignedPerson}
                            items={tasksStore.availableAssignees.map((x) => x.fullName || x.userName)} // temporary OR until api will always return fullName
                            defaultItemIndex={getTaskAssigneeItemIndex(tasksStore.selectedTask!.assignee)}
                            isConfirmationRequired
                            confirmationMessage={
                                authorizationStore.currentUser?.businessRole === BusinessRole.DP
                                    ? pl.taskDetails.assignee.changeByDPConfirmation
                                    : pl.taskDetails.assignee.changeByMSConfirmation
                            }
                            isMobile={props.isMobile}
                            editModeButtons={[EditConfirmationButtonsTypes.Cancel]}
                        />
                        {renderDivider()}
                        <TaskDetailsDropdownItem
                            onChange={(index) => updateTaskStatusDropDown(index)}
                            label={pl.taskDetails.labels.status}
                            items={taskStatusesStore.taskStatuses["TASK_STATUS"].map((x) => x.description)}
                            defaultItemIndex={getTaskStatusItemIndex(tasksStore.selectedTask!.status.id)}
                            secondaryText={formatDate(tasksStore.selectedTask!.statusDate)}
                            isBusy={tasksStore.isSelectedTaskStatusPending}
                            canBeChangedFromOutside
                            isMobile={props.isMobile}
                            editModeButtons={[EditConfirmationButtonsTypes.Cancel]}
                        />
                        <Divider size={1} />
                        <TaskDetailsItem
                            label={pl.taskDetails.labels.agency}
                            primaryText={`${tasksStore.selectedTask!.intermediary.name} / ${
                                tasksStore.selectedTask!.intermediary.businessSymbol
                            }`}
                            isMobile={props.isMobile}
                            onlyDescription
                            additionalClassName={props.isMobile ? styles.mobileDescription : ""}
                        />
                        {!!tasksStore.selectedTask!.sellerInformation && (
                            <>
                                {renderDivider()}
                                <TaskDetailsItem
                                    label={pl.taskDetails.labels.ofwca}
                                    primaryText={tasksStore.selectedTask!.sellerInformation ?? ""}
                                    isMobile={props.isMobile}
                                    onlyDescription
                                    additionalClassName={props.isMobile ? styles.mobileDescription : ""}
                                />
                            </>
                        )}
                        {!!tasksStore.selectedTask!.recommendedContactType && (
                            <>
                                {renderDivider()}
                                <TaskDetailsItem
                                    label={pl.taskDetails.labels.recommendedContactType}
                                    primaryText={tasksStore.selectedTask!.recommendedContactType ?? ""}
                                    isMobile={props.isMobile}
                                    onlyDescription
                                    additionalClassName={props.isMobile ? styles.mobileDescription : ""}
                                />
                            </>
                        )}
                        {renderDivider()}
                        <TaskDetailsDropdownItem
                            onChange={(index) =>
                                updateAttribute("CONTACT_TYPE", index, tasksStore.selectedTask!.contactType)
                            }
                            label={pl.taskDetails.labels.contactType}
                            items={taskStatusesStore.taskStatuses["CONTACT_TYPE"].map((x) => x.description)}
                            defaultItemIndex={getAttributeDefaultItemIndex(tasksStore.selectedTask!.contactType)}
                            isMobile={props.isMobile}
                            hidden={taskHasInformativeType}
                            editModeButtons={[EditConfirmationButtonsTypes.Cancel]}
                        />
                        {renderDivider()}
                        {!!tasksStore.selectedTask!.autoCloseAfter && (
                            <TaskDetailsItem
                                label={pl.taskDetails.labels.autoCloseAfter}
                                primaryText={formatDate(tasksStore.selectedTask!.autoCloseAfter)}
                                isMobile={props.isMobile}
                                onlyDescription
                                additionalClassName={props.isMobile ? styles.mobileDescription : ""}
                            />
                        )}
                        <TaskDetailsEditableDateItem
                            label={pl.taskDetails.labels.plannedDate}
                            date={tasksStore.selectedTask!.plannedDate}
                            onChange={(date) => updatePlannedDate(date as Date)}
                            isMobile={props.isMobile}
                            hidden={taskHasInformativeType}
                        />
                        {renderDivider()}
                        <TaskDetailsDropdownItem
                            label={pl.taskDetails.labels.causes}
                            items={getTaskStatusesForTaskType(
                                constants.attributeCodes.diagnosis,
                                tasksStore.selectedTask!.type.code
                            ).map((x) => x.description)}
                            isMobile={props.isMobile}
                            hidden={taskHasInformativeType}
                            multiselect
                            selectedValues={tasksStore.selectedTask!.diagnosis?.action.map((a) => a.description)}
                            editModeButtons={[EditConfirmationButtonsTypes.Accept, EditConfirmationButtonsTypes.Cancel]}
                            onItemsChange={async (values) => {
                                return updateMultiAttributeFilteredDictionary(
                                    constants.attributeCodes.diagnosis,
                                    tasksStore.selectedTask!.type.code,
                                    values,
                                    tasksStore.selectedTask!.diagnosis
                                );
                            }}
                        />
                        {renderDivider()}
                        <TaskDetailsDropdownItem
                            label={pl.taskDetails.labels.implementedActions}
                            items={getTaskStatusesForTaskType(
                                constants.attributeCodes.implementedAction,
                                tasksStore.selectedTask!.type.code
                            ).map((x) => x.description)}
                            isMobile={props.isMobile}
                            hidden={taskHasInformativeType}
                            multiselect
                            selectedValues={tasksStore.selectedTask!.implementedAction?.action.map(
                                (a) => a.description
                            )}
                            editModeButtons={[EditConfirmationButtonsTypes.Accept, EditConfirmationButtonsTypes.Cancel]}
                            onItemsChange={async (values) => {
                                return updateMultiAttributeFilteredDictionary(
                                    constants.attributeCodes.implementedAction,
                                    tasksStore.selectedTask!.type.code,
                                    values,
                                    tasksStore.selectedTask!.implementedAction
                                );
                            }}
                        />
                        {isQuickActionsAvailable() && (
                            <>
                                {renderDivider()}
                                <TaskDetailsNodeItem
                                    label={""}
                                    isMobile={props.isMobile}
                                    children={getQuickActions()}
                                    isLoading={tasksStore.isSelectedTaskStatusPending}
                                />
                            </>
                        )}
                        <Divider size={1} />
                        <Flex column gap="gap.medium">
                            <Flex gap="gap.medium" column>
                                {tasksStore.selectedTask!.comments.map((comment) => (
                                    <Comment
                                        key={`comment_${comment.id}`}
                                        comment={comment}
                                        isMobile={props.isMobile}
                                    />
                                ))}
                            </Flex>
                            <Flex column hAlign="end">
                                <TextArea
                                    resize={"vertical"}
                                    fluid
                                    placeholder={pl.taskDetails.comment.placeholder}
                                    onChange={(event) =>
                                        setComment((event.currentTarget as EventTarget & HTMLTextAreaElement).value)
                                    }
                                />
                                <Button
                                    icon={<SendIcon />}
                                    text
                                    iconOnly
                                    title={pl.taskDetails.comment.add}
                                    onClick={async () => {
                                        await tasksStore.addComment(tasksStore.selectedTask!.id, comment);

                                        if (!tasksStore.addCommentRequestStatus.error) {
                                            await tasksStore.loadTask(tasksStore.selectedTask!.id);
                                        }
                                    }}
                                />
                            </Flex>
                        </Flex>
                        {taskHasInformativeType === false && (
                            <>
                                <Divider size={1} />
                                <Flex className={classNames({ [styles.attachmentWrapper]: props.isMobile })}>
                                    <Flex className={styles.attachmentLabel}>
                                        <Text content={pl.taskDetails.labels.attachments} weight="bold" />
                                    </Flex>
                                    <Flex gap="gap.small" column fill>
                                        {tasksStore.selectedTask!.attachmentList.map((x) => (
                                            <Attachment
                                                attachment={x}
                                                isMobile={props.isMobile}
                                                onRemove={() => removeAttachment(x.id)}
                                                onClick={() => {
                                                    getFile(x.file.file, x.file.hash);
                                                }}
                                            />
                                        ))}
                                        {props.isMobile ? (
                                            <MobileUpload
                                                onUpload={async (files: File[]) => uploadAttachment(files)}
                                                fileSizeLimit={configStore.config!.fileSizeLimit}
                                            />
                                        ) : (
                                            <DropUpload
                                                onUpload={async (files: File[]) => uploadAttachment(files)}
                                                fileSizeLimit={configStore.config!.fileSizeLimit}
                                            />
                                        )}
                                    </Flex>
                                </Flex>
                            </>
                        )}
                    </Flex>
                </Ref>
            </>
        );
    };

    return (
        <Ref innerRef={taskDetailsRef}>
            <Segment style={{ padding: props.isMobile ? 0 : "1em" }} className={styles.contentWrapper}>
                {isLoadingTaskDetails ||
                tasksStore.selectedTask === null ||
                tasksStore.removeAttachmentRequestStatus.isPending ||
                tasksStore.getFileTokenRequestStatus.isPending ||
                tasksStore.retreiveFileRequestStatus.isPending
                    ? renderLoader()
                    : renderContent()}
            </Segment>
        </Ref>
    );
});

export default TasksDetails;
